// Small client-side CSV export helper.
// Used by the customer list, segment drill-downs and reminder panels so staff
// can take a list away as a spreadsheet. No server round-trip beyond fetching
// the rows, so the existing JWT auth keeps working.

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

const escapeCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Neutralise spreadsheet formula injection (=, +, -, @ at the start).
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

export const toCsv = <T,>(rows: T[], columns: CsvColumn<T>[]): string => {
  const header = columns.map((c) => escapeCell(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => escapeCell(c.value(row))).join(','));
  return [header, ...body].join('\r\n');
};

export const downloadCsv = (filename: string, csv: string): void => {
  // Prefix a BOM so Excel opens UTF-8 (₹, names with accents) correctly.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download a VCARD (.vcf) so a phone offers "Add to contacts" directly.
 * This is the mobile-friendly path: a customer cannot scan a QR shown on their
 * own screen, so instead of a QR we hand them the card as a file.
 */
export const downloadVCard = (filename: string, vcard: string): void => {
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename.endsWith('.vcf') ? filename : `${filename}.vcf`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const withExt = (filename: string, ext: string): string =>
  filename.toLowerCase().endsWith(ext) ? filename : `${filename}${ext}`;

/**
 * Share a VCARD using the phone's native share sheet ("Add to contacts",
 * WhatsApp, Bluetooth, …). Falls back to a WhatsApp link, then a plain
 * download, so every device has a working path.
 */
export const shareVCard = async (
  filename: string,
  vcard: string,
  title: string,
  fallbackUrl?: string,
): Promise<void> => {
  const nav = navigator as any;
  const fileName = withExt(filename, '.vcf');
  try {
    const file = new File([vcard], fileName, { type: 'text/vcard' });
    if (nav?.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], title, text: title });
      return;
    }
  } catch {
    // cancelled or unsupported — try a plain link share next
  }
  try {
    if (nav?.share) {
      await nav.share({ title, text: title, url: fallbackUrl });
      return;
    }
  } catch {
    // cancelled — do not fall through to a second popup
    return;
  }
  if (fallbackUrl) {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${title} ${fallbackUrl}`)}`, '_blank');
  } else {
    downloadVCard(filename, vcard);
  }
};

/** Share plain text/URL via the native sheet, falling back to WhatsApp. */
export const shareText = async (title: string, text: string, url?: string): Promise<void> => {
  const nav = navigator as any;
  try {
    if (nav?.share) {
      await nav.share({ title, text, url });
      return;
    }
  } catch {
    return;
  }
  const payload = url ? `${text} ${url}` : text;
  window.open(`https://wa.me/?text=${encodeURIComponent(payload)}`, '_blank');
};

/** Share a rendered QR canvas as a PNG via the native sheet, else download it. */
export const shareCanvasImage = async (
  canvas: HTMLCanvasElement,
  filename: string,
  title: string,
): Promise<void> => {
  const fileName = withExt(filename, '.png');
  const nav = navigator as any;
  const dataUrl = canvas.toDataURL('image/png');
  try {
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob) {
      const file = new File([blob], fileName, { type: 'image/png' });
      if (nav?.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title });
        return;
      }
    }
    if (nav?.share) {
      await nav.share({ title, url: dataUrl });
      return;
    }
  } catch {
    // cancelled or unsupported — fall through to download
  }
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatDate = (value: unknown): string => {
  if (!value) return '';
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Builds YYYY-MM-DD for filename-safe, sortable names. */
export const todayStamp = (): string => new Date().toISOString().slice(0, 10);
